import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import {DocumentNode} from 'graphql';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessTaskStatusType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import getProcedureUIString from 'procedures/ProceduresStringKeys';
import {navigateToStudyOverviewTab} from 'studies/FileUtils';
import {getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';
import * as Colors from 'ui-library/Colors';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Pane, {PaneBorder} from 'ui-library/pane/Pane';
import * as DateUtil from 'util/DateUtil';

import {RenderTaskStatusWithIcon} from '../shared/ProcedureRunTableUtil';
import {Body, Cell, Header, HeaderCell, Row, Table, Title} from '../StudyTableComponent';
import {StudyInaccessibleContentBanner, StudyQueryErrorBanner} from '../utils/ui/StudyQueryBanners';
import {
  MultiProcedureRunListingTableQuery,
  MultiProcedureRunListingTableQueryVariables,
  MultiProcedureRunListingTableQuery_study_StudyNode_activeProcedureRuns as ProcedureRunNode,
  MultiProcedureRunListingTableQuery_study_StudyNode_activeProcedureRuns_ProcedureRunNode as AccessibleProcedureRunNode,
  MultiProcedureRunListingTableQuery_study_StudyNode_activeProcedureRuns_ProcedureRunNode_processFlowchartTaskGroup as ProcessFlowchartTaskGroup,
} from './__generated/MultiProcedureRunListingTableQuery';
import NoExperimentOverview from './NoExperimentOverview';

export const MULTI_PROCEDURE_RUN_LISTING_TABLE_QUERY: DocumentNode = gql`
  query MultiProcedureRunListingTableQuery($studyId: StrictString!, $maxNumProcedureRuns: StrictInt!)
  @skipAudit
  @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        studySchema {
          id
          studyType
        }
        activeProcedureRuns(limit: $maxNumProcedureRuns) {
          ... on ProcedureRunNode {
            id
            name
            description
            displayId
            createdAt
            processFlowchartTaskGroup {
              ... on ProcessNode {
                id
                taskStatusCounts {
                  status {
                    id
                    statusType
                  }
                }
              }
              ...InaccessibleInfoGraphQL
            }
            templateProcedureFlowchartVersion {
              ... on ProcedureFlowchartVersionNode {
                id
                procedureFlowchart {
                  ... on ProcedureFlowchartNode {
                    id
                    parent {
                      id
                    }
                  }
                }
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ProcedureRunOverview {
  id: string;
  name: string;
  description: string;
  displayId: string | null;
  procedureTemplateId: string;
  createdAt: string;
  processFlowchartTaskGroup: ProcessFlowchartTaskGroup | null;
  flowchartVersionId?: string;
}

interface ProcedureRunOverviewsAndInfo {
  procedureRunOverviews: Array<ProcedureRunOverview>;
  hasInaccessibleProcedureRuns: boolean;
}

function getProcedureRunOverviews(procedureRuns: Array<ProcedureRunNode>): ProcedureRunOverviewsAndInfo {
  const procedureRunOverviews: Array<ProcedureRunOverview> = [];
  const accessibleProcedureRuns = procedureRuns.filter(
    (procedureRun) =>
      !isInaccessible(procedureRun) && !isInaccessible(procedureRun.templateProcedureFlowchartVersion)
  ) as Array<AccessibleProcedureRunNode>;
  for (const procedureRun of accessibleProcedureRuns) {
    // asserting for typing purposes
    assert(
      !isInaccessible(procedureRun.templateProcedureFlowchartVersion),
      'Expected template procedure flowchart version to be accessible.'
    );
    // Procedure flowchart parent is null when the type is procedure run, but we expect it to be of type
    // procedure here as the procedure holds the template
    assert(
      procedureRun.templateProcedureFlowchartVersion.procedureFlowchart?.parent != null,
      'Expected procedure flowchart parent to be non-null.'
    );
    procedureRunOverviews.push({
      ...procedureRun,
      procedureTemplateId: procedureRun.templateProcedureFlowchartVersion.procedureFlowchart.parent.id,
      flowchartVersionId: procedureRun.templateProcedureFlowchartVersion.id,
    });
  }
  return {
    procedureRunOverviews,
    hasInaccessibleProcedureRuns: procedureRuns.length > accessibleProcedureRuns.length,
  };
}
interface MultiProcedureRunParamsTableProps {
  studyId: string;
}

export default function MultiProcedureRunListingTable({
  studyId,
}: MultiProcedureRunParamsTableProps): JSX.Element {
  const result = useBenchlingQuery<
    MultiProcedureRunListingTableQuery,
    MultiProcedureRunListingTableQueryVariables
  >(MULTI_PROCEDURE_RUN_LISTING_TABLE_QUERY, {
    variables: {studyId, maxNumProcedureRuns: BenchlingConfig.STUDIES_MAX_NUM_RUNS_PER_PD_STUDY},
    fetchPolicy: 'cache-and-network',
  });

  if (result.type === QueryResultType.PENDING) {
    return (
      <>
        <div className={css(styles.loadingDiv)} />
        <CenteredHorizontalThrobber />
      </>
    );
  } else if (result.type === QueryResultType.ERROR) {
    return <StudyQueryErrorBanner />;
  } else {
    const {data} = result;
    if (isInaccessible(data.study)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.study}
          modelType={ModelTypeConstants.STUDY}
          displayErrorText="This study is no longer accessible. Try refreshing the page if this is unexpected."
        />
      );
    }
    const {procedureRunOverviews, hasInaccessibleProcedureRuns} = getProcedureRunOverviews(
      data.study.activeProcedureRuns
    );
    return (
      <>
        {hasInaccessibleProcedureRuns && (
          <div className={css(styles.inaccessibleProcedureRunBanner)}>
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              text={`One or more ${getProcedureUIString(
                ProceduresStringKeys.PROCEDURE,
                getProcedureTypeForStudyType(data.study.studySchema.studyType),
                {
                  stringCase: StringCase.LOWER,
                }
              )} runs are hidden. Request access from your admin to view.`}
            />
          </div>
        )}
        <RenderMultiProcedureRunListingTable
          studyId={studyId}
          studyType={data.study.studySchema.studyType}
          procedureRuns={procedureRunOverviews}
        />
      </>
    );
  }
}

// exported for testing
export function getProcedureRunStatusComponent({
  processFlowchartTaskGroup,
}: {
  processFlowchartTaskGroup: ProcessFlowchartTaskGroup | null;
}): JSX.Element | string {
  if (processFlowchartTaskGroup == null || isInaccessible(processFlowchartTaskGroup)) {
    return '';
  }
  const hasFailed = processFlowchartTaskGroup.taskStatusCounts.some(
    (task) => task.status.statusType === ProcessTaskStatusType.FAILED
  );
  if (hasFailed) {
    return <RenderTaskStatusWithIcon statusType={ProcessTaskStatusType.FAILED} />;
  }
  const isInProgress = processFlowchartTaskGroup.taskStatusCounts.some(
    (task) =>
      task.status.statusType === ProcessTaskStatusType.PENDING ||
      task.status.statusType === ProcessTaskStatusType.IN_PROGRESS
  );
  return (
    <RenderTaskStatusWithIcon
      statusType={isInProgress ? ProcessTaskStatusType.IN_PROGRESS : ProcessTaskStatusType.COMPLETED}
    />
  );
}

interface RenderMultiProcedureRunParamsTableProps {
  studyId: string;
  studyType: StudyType;
  procedureRuns: Array<ProcedureRunOverview>;
}

// Renders the description inside a Cell and shows a tooltip only when the text is truncated.
function DescriptionCellWithTooltip({description}: {description: string | null}): JSX.Element {
  const descriptionText = description ?? '';
  const textRef = React.useRef<HTMLDivElement>(null);
  const [isEllipsized, setIsEllipsized] = React.useState(false);

  React.useLayoutEffect(() => {
    if (textRef.current != null) {
      setIsEllipsized(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [descriptionText]);

  return (
    <Cell className={css(styles.descriptionCell)}>
      <Tooltip
        text={descriptionText}
        placement={FloatingPlacement.TOP}
        isDisabled={!isEllipsized}
        toggleDisplayMode={ToggleDisplayMode.BLOCK_FIT_CONTENT}
      >
        <div ref={textRef} className={css(styles.descriptionCell)}>
          {descriptionText}
        </div>
      </Tooltip>
    </Cell>
  );
}

export function RenderMultiProcedureRunListingTable({
  studyId,
  studyType,
  procedureRuns,
}: RenderMultiProcedureRunParamsTableProps): JSX.Element {
  const procedureRunTypeSpecificTitle = `${getProcedureUIString(
    ProceduresStringKeys.PROCEDURE,
    getProcedureTypeForStudyType(studyType),
    {
      stringCase: StringCase.SENTENCE,
    }
  )} runs`;
  return procedureRuns.length === 0 ? (
    // Pane has a hardcoded flex-grow: 1, so we need to wrap it in a div to prevent it from taking up the full height
    <div>
      <Pane title={procedureRunTypeSpecificTitle} paneBorder={PaneBorder.SOLID} fitContent={true}>
        <div className={css(styles.noExperimentViewInnerContainer)}>
          <NoExperimentOverview studyId={studyId} studyType={studyType} />
        </div>
      </Pane>
    </div>
  ) : (
    <Table
      dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'table'}}
      scrollContents={true}
    >
      <Title>{procedureRunTypeSpecificTitle}</Title>
      <Header className={css(styles.headerRowStyle)}>
        <HeaderCell className={css(styles.headerNameCell)}> Name </HeaderCell>
        <HeaderCell className={css(styles.headerIdCell)}> ID </HeaderCell>
        <HeaderCell className={css(styles.headerstatusCell)}> Status </HeaderCell>
        <HeaderCell className={css(styles.headerCreatedAtCell)}> Created </HeaderCell>
        <HeaderCell className={css(styles.headerRecipeChipCell)}>
          {' '}
          {getProcedureUIString(ProceduresStringKeys.PROCEDURE, getProcedureTypeForStudyType(studyType), {
            stringCase: StringCase.SENTENCE,
          })}{' '}
        </HeaderCell>
        <HeaderCell className={css(styles.headerDescriptionCell)}> Description </HeaderCell>
      </Header>
      <Body>
        {procedureRuns.map((procedureRun) => (
          <Row
            key={procedureRun.id}
            onClick={() => {
              navigateToStudyOverviewTab(studyId, procedureRun.id);
            }}
            className={css(styles.rowStyle)}
            dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'row'}}
          >
            <Cell
              className={css(styles.nameCell)}
              dataTestAttrs={{component: 'MultiProcedureRunListingTable', element: 'nameCell'}}
            >
              {procedureRun.name}
            </Cell>
            <Cell className={css(styles.IdCell)}>{procedureRun.displayId}</Cell>
            <Cell className={css(styles.statusCell)}>
              {getProcedureRunStatusComponent({
                processFlowchartTaskGroup: procedureRun.processFlowchartTaskGroup,
              })}
            </Cell>
            <Cell className={css(styles.headerCreatedAtCell)}>
              <div className={css(styles.createdAtCell)}>
                {DateUtil.readableTime(procedureRun.createdAt, {showTime: true})}
              </div>
            </Cell>
            <Cell className={css(styles.recipeChipCell)}>
              <ProcedureChip
                id={procedureRun.procedureTemplateId}
                flowchartVersionId={procedureRun.flowchartVersionId}
                size={ChipSize.MINI}
              />
            </Cell>
            <DescriptionCellWithTooltip description={procedureRun.description} />
          </Row>
        ))}
      </Body>
    </Table>
  );
}

const styles = StyleSheet.create({
  createdAtCell: {
    flex: '0 0 12%',
    alignContent: 'center',
    ...FONT_PROXIMA_13,
    display: 'flex',
    alignItems: 'center',
  },
  nameCell: {
    flex: '0 0 15%',
    alignContent: 'center',
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  statusCell: {
    flex: '0 0 10%',
    alignContent: 'center',
    ...FONT_PROXIMA_13,
  },
  IdCell: {
    flex: '0 0 5%',
    alignContent: 'center',
    ...FONT_PROXIMA_13,
  },
  recipeChipCell: {
    flex: '0 0 15%',
    alignContent: 'center',
    ...FONT_PROXIMA_13,
  },
  loadingDiv: {
    paddingBottom: 5,
  },
  rowStyle: {
    width: '100%',
    minHeight: 44,
  },
  headerRowStyle: {
    height: 32,
    alignContent: 'center',
    color: Colors.GRAY_SEMIDARK,
    ...FONT_PROXIMA_13,
  },
  headerCreatedAtCell: {
    flex: '0 0 12%',
  },
  headerNameCell: {
    flex: '0 0 15%',
  },
  headerDescriptionCell: {
    flex: '0 0 35%',
  },
  headerstatusCell: {
    flex: '0 0 10%',
  },
  headerIdCell: {
    flex: '0 0 5%',
  },
  headerRecipeChipCell: {
    flex: '0 0 15%',
  },
  noExperimentViewInnerContainer: {
    backgroundColor: Color.GRAY_100,
    padding: '40px 0px 40px 0px',
  },
  inaccessibleProcedureRunBanner: {
    padding: 4,
    backgroundColor: Color.WHITE,
  },
  descriptionCell: {
    flex: '0 0 35%',
    ...FONT_PROXIMA_13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
});
